import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { navItems } from '@/constants/site'

export const Header: FC = () => {
    const [scrolled, setScrolled] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        let last = window.scrollY
        let frameId = 0

        const update = () => {
            frameId = 0

            const y = window.scrollY

            setScrolled(y > 24)

            if (Math.abs(y - last) > 6) {
                setHidden(y > last && y > 320)
                last = y
            }
        }

        const onScroll = () => {
            if (!frameId) frameId = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
            cancelAnimationFrame(frameId)
        }
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = ''
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-[transform,background-color,border-color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                hidden && !open ? '-translate-y-full' : 'translate-y-0',
                scrolled || open ? 'border-b border-white/[0.07] bg-background/75 backdrop-blur-xl' : 'border-b border-transparent'
            )}
        >
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8 lg:h-[72px]'>
                <a href='#hero' className='rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand' aria-label="FORQ home">
                    <Logo />
                </a>

                <nav aria-label="Main" className='hidden items-center gap-1 lg:flex'>
                    {navItems.map(item => (
                        <a
                            key={item.href}
                            href={item.href}
                            className='rounded-full px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-2 focus-visible:outline-brand'
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href='#quote'
                        className='group hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-foreground shadow-[0_0_0_1px_oklch(1_0_0/0.1)_inset] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_var(--brand)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:inline-flex'
                    >
                        Get a quote
                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                    </a>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? "Close menu" : "Open menu"}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-brand lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:hidden',
                    open ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0'
                )}
                inert={!open}
            >
                <div className='overflow-hidden'>
                    <nav aria-label="Mobile" className='flex h-[calc(100svh-4rem)] flex-col gap-1 px-5 pb-8 pt-4 sm:px-8'>
                        {navItems.map((item, index) => (
                            <a
                                key={item.href}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className='flex items-center justify-between border-b border-white/[0.07] py-5 font-heading font-expanded text-2xl font-bold text-white transition-colors hover:text-brand'
                                style={{ transitionDelay: open ? `${index * 40}ms` : '0ms' }}
                            >
                                {item.label}
                                <ArrowRight className='size-5 text-white/40' />
                            </a>
                        ))}

                        <a
                            href='#quote'
                            onClick={() => setOpen(false)}
                            className='mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-4 text-base font-semibold text-brand-foreground'
                        >
                            Get a quote
                            <ArrowRight className='size-4' />
                        </a>
                    </nav>
                </div>
            </div>
        </header>
    )
}
